import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { WorkTypeIcon } from './KanbanBoard';
import { useImagePaste } from '../hooks/useImagePaste';
import RichEditor from './RichEditor';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const LABEL = "text-[11px] uppercase tracking-[0.12em] text-zinc-600 font-bold";

// Top-level work items are Stories or Bugs only. "Task" is reserved for
// subtasks created from inside a Story/Bug detail page (mirrors Jira).
const WORK_TYPES = [
  { value: 'story', label: 'Story', activeClass: 'border-emerald-500 bg-emerald-50 text-emerald-700', desc: 'User story or feature' },
  { value: 'bug', label: 'Bug', activeClass: 'border-red-500 bg-red-50 text-red-700', desc: 'Defect or issue' },
];

const DEFAULT_STATUSES = [
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'in_review', label: 'In Review' },
  { value: 'done', label: 'Done' },
];

// Renders a single custom field (from project template) — select / number / date / textarea / text / richtext.
function CustomFieldInput({ def, value, onChange, uploadImage }) {
  if (def.type === 'select') {
    return (
      <Select value={value || ''} onValueChange={(v) => onChange(v === '__clear__' ? '' : v)}>
        <SelectTrigger className="mt-1.5 h-9" data-testid={`cf-${def.field_id}`}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__clear__">— None —</SelectItem>
          {(def.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (def.type === 'number') {
    return <Input type="number" min="0" value={value ?? ''} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} className="mt-1.5 h-9" data-testid={`cf-${def.field_id}`} />;
  }
  if (def.type === 'date') {
    // Block selecting a date in the past for due-like fields.
    const today = new Date().toISOString().split('T')[0];
    return <Input type="date" min={today} value={value || ''} onChange={e => onChange(e.target.value)} className="mt-1.5 h-9" data-testid={`cf-${def.field_id}`} />;
  }
  if (def.type === 'url') {
    return <Input type="url" placeholder="https://…" value={value || ''} onChange={e => onChange(e.target.value)} className="mt-1.5 h-9" data-testid={`cf-${def.field_id}`} />;
  }
  if (def.type === 'textarea' || def.type === 'long_text') {
    return <Textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={3} className="mt-1.5 resize-none" data-testid={`cf-${def.field_id}`} />;
  }
  if (def.type === 'richtext') {
    return (
      <div className="mt-1.5 border border-zinc-200 rounded-md overflow-hidden focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all" data-testid={`cf-${def.field_id}`}>
        <RichEditor value={value || ''} onChange={onChange} uploadImage={uploadImage} placeholder="Write content…" minHeight={100} />
      </div>
    );
  }
  return <Input value={value || ''} onChange={e => onChange(e.target.value)} className="mt-1.5" data-testid={`cf-${def.field_id}`} />;
}

export default function CreateTaskDialog({ open, onClose, projectId, project, sprints, members, onCreated, defaultStatus, defaultSprintId }) {
  const { getOrgHeaders, API } = useAuth();
  const { uploadImage } = useImagePaste(getOrgHeaders);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // Work type intentionally starts NULL — Jira/Asana style. Users must
  // explicitly pick Story vs Bug before any other field is editable, so
  // teams don't auto-default to "Story" and forget to switch for defects.
  const [workType, setWorkType] = useState(null);
  const [status, setStatus] = useState(defaultStatus || 'todo');
  const [priority, setPriority] = useState('medium');
  const [assigneeId, setAssigneeId] = useState('unassigned');
  const [sprintId, setSprintId] = useState('__none__');
  const [dueDate, setDueDate] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [customValues, setCustomValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Sync defaults whenever the dialog (re)opens.
  useEffect(() => {
    if (!open) return;
    setStatus(defaultStatus || 'todo');
    setSprintId(defaultSprintId ? defaultSprintId : '__none__');
    // Force users to re-pick the work type each time the dialog opens.
    setWorkType(null);
    setError('');
  }, [open, defaultStatus, defaultSprintId]);

  const statusOptions = useMemo(() => {
    // Default statuses are ALWAYS available. custom_statuses are additive per-project.
    const extras = Array.isArray(project?.custom_statuses)
      ? project.custom_statuses.map(s => ({ value: s.id || s.value, label: s.label || s.name }))
      : [];
    return [...DEFAULT_STATUSES, ...extras];
  }, [project]);

  const sprintOptions = useMemo(() => {
    return (sprints || []).filter(s => s.status !== 'completed');
  }, [sprints]);

  // Custom fields are scoped per work_type via the `work_types` array on each
  // field definition. An empty array means "applies to all types". We
  // re-derive on every workType change so a field marked Story-only doesn't
  // leak into Bug/Task creation forms (and vice versa).
  const customFieldDefs = (project?.custom_fields || []).filter(
    cf => !cf.work_types?.length || cf.work_types.includes(workType)
  );

  // Promote two well-known fields to their own slots in the form so users
  // never miss them. Detection is by lowercased name (matches what every
  // template seeds today). Anything else falls through to "Additional
  // details" at the bottom.
  const acceptanceField = customFieldDefs.find(f => /acceptance\s*criteria/i.test(f.name));
  // Treat Story Points as a free-form number ALWAYS, regardless of the
  // type stored on the project. Old projects may still have type='select'
  // (Fibonacci options) on the field definition; this normalisation lets
  // us flip the UI immediately to a number input without forcing a server
  // migration on every customer's DB first.
  const storyPointsField = customFieldDefs.find(f => /story\s*points?/i.test(f.name));
  const spFieldNormalized = storyPointsField
    ? { ...storyPointsField, type: 'number', options: [] }
    : null;
  const promotedIds = new Set(
    [acceptanceField?.field_id, storyPointsField?.field_id].filter(Boolean)
  );
  const otherCustomFields = customFieldDefs.filter(f => !promotedIds.has(f.field_id));

  const reset = () => {
    setTitle(''); setDescription(''); setWorkType(null);
    setPriority('medium'); setAssigneeId('unassigned');
    setSprintId('__none__');
    setDueDate(''); setTagsInput(''); setCustomValues({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!workType) { setError('Please select an item type — Story or Bug'); return; }
    if (!title.trim()) { setError('Title is required'); return; }
    setSubmitting(true);
    setError('');
    try {
      // Clean custom values: drop empty strings to avoid storing noise.
      const cleanCf = Object.fromEntries(
        Object.entries(customValues).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      );
      await axios.post(`${API}/projects/${projectId}/tasks`, {
        title: title.trim(),
        description,
        work_type: workType,
        priority,
        status,
        assignee_id: assigneeId === 'unassigned' ? null : assigneeId,
        sprint_id: sprintId === '__none__' ? null : sprintId,
        due_date: dueDate || null,
        tags: tagsInput.split(',').map(t => t.trim()).filter(Boolean),
        custom_fields: cleanCf,
      }, { withCredentials: true, headers: getOrgHeaders() });
      reset();
      onCreated();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.detail || 'Failed to create. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl p-0 max-h-[92vh] flex flex-col" data-testid="create-task-dialog">
        <DialogHeader className="px-7 pt-5 pb-4 flex-shrink-0 border-b border-zinc-100">
          <DialogTitle className="font-['Outfit'] text-[18px] font-bold text-zinc-900 tracking-tight">Create work item</DialogTitle>
          <DialogDescription className="text-[12px] text-zinc-500">Pick a type, write a clear title, and fill in just the fields you need today.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto px-7 py-5 space-y-5" data-testid="create-task-scroll">
            {/* WORK TYPE — required, no default. Slightly oversized buttons
                with a clear "pick one" prompt so it's never accidentally
                skipped. */}
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <Label className={LABEL}>Item type <span className="text-red-500 ml-0.5">*</span></Label>
                {!workType && <span className="text-[10.5px] text-amber-600 font-semibold">Choose one to continue</span>}
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                {WORK_TYPES.map(t => (
                  <button key={t.value} type="button" data-testid={`work-type-${t.value}`} onClick={() => { setWorkType(t.value); setError(''); }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all duration-150
                      ${workType === t.value
                        ? `${t.activeClass} shadow-sm`
                        : workType === null
                          ? 'border-amber-200 bg-amber-50/30 text-zinc-600 hover:border-amber-300 hover:bg-amber-50/60'
                          : 'border-zinc-200 bg-white text-zinc-500 hover:border-zinc-300'}`}>
                    <WorkTypeIcon type={t.value} size={22} />
                    <div className="text-left">
                      <p className="text-[13px] font-bold leading-tight">{t.label}</p>
                      <p className={`text-[10px] leading-tight mt-0.5 ${workType === t.value ? 'opacity-75' : 'text-zinc-400'}`}>{t.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Everything below is locked behind a work-type pick — visually
                dimmed so the user's eye is drawn upward to the type
                selection until they make a choice. */}
            <div className={workType ? '' : 'opacity-50 pointer-events-none transition-opacity'}>

              {/* TITLE + DESCRIPTION — primary content, full width */}
              <div className="space-y-4">
                <div>
                  <Label className={LABEL}>Title <span className="text-red-500 ml-0.5">*</span></Label>
                  <Input data-testid="create-task-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="What needs to be done?" required className="mt-1.5 h-10 text-[14px]" />
                </div>

                <div>
                  <Label className={LABEL}>Description</Label>
                  <div className="mt-1.5 border border-zinc-200 rounded-md overflow-hidden focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                    <RichEditor
                      value={description}
                      onChange={setDescription}
                      uploadImage={uploadImage}
                      placeholder="Add more details… Paste screenshots inline."
                      minHeight={120}
                    />
                  </div>
                </div>

                {/* Acceptance Criteria — promoted directly under description for
                    Stories so it can't be missed. Renders as the same rich-text
                    editor so authors get bullets / checklists / image paste. */}
                {acceptanceField && (
                  <div data-testid="create-task-acceptance">
                    <Label className={LABEL}>{acceptanceField.name}</Label>
                    <div className="mt-1.5 border border-zinc-200 rounded-md overflow-hidden focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                      <RichEditor
                        value={customValues[acceptanceField.field_id] || ''}
                        onChange={(v) => setCustomValues(prev => ({ ...prev, [acceptanceField.field_id]: v }))}
                        uploadImage={uploadImage}
                        placeholder="• Given …  • When …  • Then …"
                        minHeight={100}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* DETAILS SECTION — compact 3-col grid for the metadata fields */}
              <div className="mt-6 pt-5 border-t border-zinc-100">
                <p className="text-[10px] uppercase tracking-[0.14em] text-zinc-400 font-bold mb-3">Details</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className={LABEL}>Status</Label>
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger data-testid="create-task-status" className="mt-1.5 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {statusOptions.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={LABEL}>Priority</Label>
                    <Select value={priority} onValueChange={setPriority}>
                      <SelectTrigger data-testid="create-task-priority" className="mt-1.5 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={LABEL}>Sprint</Label>
                    <Select value={sprintId} onValueChange={setSprintId}>
                      <SelectTrigger data-testid="create-task-sprint" className="mt-1.5 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Backlog (no sprint)</SelectItem>
                        {sprintOptions.map(s => (
                          <SelectItem key={s.sprint_id} value={s.sprint_id}>
                            {s.name} {s.status === 'active' ? '· active' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={LABEL}>Assignee</Label>
                    <Select value={assigneeId} onValueChange={setAssigneeId}>
                      <SelectTrigger data-testid="create-task-assignee" className="mt-1.5 h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {members?.map(m => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={LABEL}>Due Date</Label>
                    <Input type="date" min={new Date().toISOString().split('T')[0]} data-testid="create-task-due-date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="mt-1.5 h-9" />
                  </div>
                  <div>
                    <Label className={LABEL}>Tags</Label>
                    <Input data-testid="create-task-tags" value={tagsInput} onChange={e => setTagsInput(e.target.value)} placeholder="frontend, api" className="mt-1.5 h-9" />
                  </div>
                  {/* Story Points — promoted into the Details grid because
                      it's a high-frequency field on Scrum stories. Narrow
                      width like the rest of the row. */}
                  {storyPointsField && (
                    <div data-testid="create-task-sp">
                      <Label className={LABEL}>{storyPointsField.name}</Label>
                      <CustomFieldInput def={spFieldNormalized}
                        value={customValues[storyPointsField.field_id]}
                        uploadImage={uploadImage}
                        onChange={(v) => setCustomValues(prev => ({ ...prev, [storyPointsField.field_id]: v }))} />
                    </div>
                  )}
                </div>
              </div>

              {/* Custom fields from project template (Severity / Estimate /
                  Steps to Reproduce, etc.). Promoted fields (Acceptance
                  Criteria, Story Points) are intentionally excluded — they
                  render in their dedicated slots above. */}
              {otherCustomFields.length > 0 && (
                <div className="mt-6 pt-5 border-t border-zinc-100">
                  <p className="text-[10px] uppercase tracking-[0.14em] text-zinc-400 font-bold mb-3">Additional details</p>
                  <div className="grid grid-cols-2 gap-3">
                    {otherCustomFields.map(def => (
                      <div key={def.field_id} className={['textarea', 'long_text', 'richtext'].includes(def.type) ? 'col-span-2' : ''}>
                        <Label className={LABEL}>{def.name}</Label>
                        <CustomFieldInput def={def}
                          value={customValues[def.field_id]}
                          uploadImage={uploadImage}
                          onChange={(v) => setCustomValues(prev => ({ ...prev, [def.field_id]: v }))} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sticky footer */}
          <div className="px-7 py-3 border-t border-zinc-100 bg-white flex-shrink-0">
            {error && <p className="text-[11px] text-red-600 mb-2" data-testid="create-task-error">{error}</p>}
            <div className="flex items-center justify-between gap-3">
              <p className="text-[10.5px] text-zinc-400">
                {workType ? <><span className="text-emerald-600 font-semibold">●</span> {WORK_TYPES.find(w => w.value === workType)?.label} selected</> : 'Pick a type to continue'}
              </p>
              <div className="flex items-center gap-2">
                <Button type="button" variant="ghost" onClick={onClose} className="h-9 px-3 text-[12px] text-zinc-600">Cancel</Button>
                <Button type="submit" data-testid="submit-create-task" disabled={submitting || !title.trim() || !workType} className="h-9 px-5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-[13px] disabled:bg-zinc-300 disabled:cursor-not-allowed">
                  {submitting ? 'Creating…' : 'Create item'}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
